import { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '@/store/store';
import { DataGrid, KeenIcon } from '@/components';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useDeleteHarborThemeRouteMutation,
  useGetHarborThemeRoutesQuery,
} from '@/services/ThemeRoutesService';
import type { ThemeRoute, ThemeRouteType } from '@/types/theme-routes.types';
import ThemeRouteFormPage from '@/pages/theme-routes/ThemeRouteFormPage';

type ViewState =
  | { mode: 'list' }
  | { mode: 'create' }
  | { mode: 'edit'; id: string }
  | { mode: 'view'; id: string };

const TourismHighlightsTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const selectedHarborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );

  const getViewStateFromSearch = useCallback((): ViewState => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('thMode');
    const id = params.get('thId');

    if (mode === 'create') return { mode: 'create' };
    if (mode === 'edit' && id) return { mode: 'edit', id };
    if (mode === 'view' && id) return { mode: 'view', id };

    return { mode: 'list' };
  }, [location.search]);

  const [viewState, setViewState] = useState<ViewState>(() =>
    getViewStateFromSearch()
  );

  const setUrlState = useCallback(
    (next: ViewState) => {
      const params = new URLSearchParams(location.search);
      params.set('tab', 'tourism-highlights');

      if (next.mode === 'list') {
        params.delete('thMode');
        params.delete('thId');
      } else if (next.mode === 'create') {
        params.set('thMode', 'create');
        params.delete('thId');
      } else if (next.mode === 'edit') {
        params.set('thMode', 'edit');
        params.set('thId', next.id);
      } else if (next.mode === 'view') {
        params.set('thMode', 'view');
        params.set('thId', next.id);
      }

      navigate({ search: params.toString() }, { replace: true });
    },
    [location.search, navigate]
  );

  const setViewStateWithUrl = useCallback(
    (next: ViewState) => {
      setViewState(next);
      setUrlState(next);
    },
    [setUrlState]
  );

  const { data: routesResponse, isLoading } = useGetHarborThemeRoutesQuery(
    selectedHarborId
      ? {
          type: 1,
          harbor_id: selectedHarborId,
        }
      : undefined
  );
  const [deleteThemeRoute] = useDeleteHarborThemeRouteMutation();

  // Access on this tab follows the tourism permissions; the backend decides per highlight (canEdit / canDelete)
  // from the harbors the highlight touches.
  const canCreate = hasPermission('tourism_create');
  const canUpdate = hasPermission('tourism_update');
  const canDelete = hasPermission('tourism_delete');

  const harborRoutes = useMemo(() => {
    return (routesResponse?.data as ThemeRoute[]) || [];
  }, [routesResponse]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!selectedHarborId) return;
      try {
        await deleteThemeRoute({ id, harborId: selectedHarborId }).unwrap();
        enqueueSnackbar('Tourism highlight deleted', { variant: 'success' });
      } catch {
        enqueueSnackbar('Failed to delete tourism highlight', {
          variant: 'error',
        });
      }
    },
    [deleteThemeRoute, enqueueSnackbar, selectedHarborId]
  );

  const columns = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }: { row: { original: ThemeRoute } }) => {
          const route = row.original;
          const translation =
            route.translations?.[route.defaultLanguage] ?? undefined;
          const label = route.name || translation?.name || '(Untitled)';

          if (route.canView === false) {
            return (
              <span className="text-sm font-medium text-gray-900">{label}</span>
            );
          }

          return (
            <button
              type="button"
              className="text-sm font-medium text-gray-900 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() =>
                setViewStateWithUrl({ mode: 'view', id: route.id })
              }
              aria-label={`View tourism highlight ${label}`}
            >
              {label}
            </button>
          );
        },
        meta: { className: 'min-w-[240px]' },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }: { row: { original: ThemeRoute } }) => (
          <span className="text-sm text-gray-800">{row.original.status}</span>
        ),
        meta: { className: 'min-w-[140px]' },
      },
      {
        header: 'POIs',
        accessorKey: 'poiCount',
        cell: ({ row }: { row: { original: ThemeRoute } }) => (
          <span className="text-sm text-gray-800">
            {(row.original.poiSequence || []).length}
          </span>
        ),
        meta: { className: 'min-w-[90px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: ThemeRoute } }) => {
          const route = row.original;
          const translation =
            route.translations?.[route.defaultLanguage] ?? undefined;
          const label = route.name || translation?.name || 'highlight';

          return (
            <div className="flex items-center gap-2">
              {route.canView !== false && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() =>
                    setViewStateWithUrl({ mode: 'view', id: route.id })
                  }
                  aria-label={`View ${label}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="eye"
                    className="text-gray-500"
                  />
                </button>
              )}

              {canUpdate && route.canEdit !== false && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() =>
                    setViewStateWithUrl({ mode: 'edit', id: route.id })
                  }
                  aria-label={`Edit ${label}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-gray-500"
                  />
                </button>
              )}

              {canDelete && route.canDelete !== false && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() => handleDelete(route.id)}
                  aria-label={`Delete ${label}`}
                >
                  <KeenIcon
                    type="filled"
                    icon="trash"
                    className="text-red-500"
                  />
                </button>
              )}
            </div>
          );
        },
        meta: { className: 'w-[160px]' },
      },
    ],
    [canDelete, canUpdate, handleDelete, setViewStateWithUrl]
  );

  if (!selectedHarborId) {
    return (
      <div className="p-4 text-center text-gray-500">
        Select a harbor to manage tourism highlights.
      </div>
    );
  }

  if (viewState.mode === 'create') {
    const type: ThemeRouteType = 'harbor';
    return (
      <ThemeRouteFormPage
        mode="create"
        fixedHarborId={selectedHarborId}
        routeType={type}
        routingMode="straight"
        backTargetOverride="/harbors/details?tab=tourism-highlights"
        permissionScope="tourism"
        enablePoiHarborConnection
        embedded
        hideComments
        onBack={() => setViewStateWithUrl({ mode: 'list' })}
      />
    );
  }

  if (viewState.mode === 'edit') {
    const type: ThemeRouteType = 'harbor';
    return (
      <ThemeRouteFormPage
        mode="edit"
        fixedHarborId={selectedHarborId}
        routeType={type}
        routingMode="straight"
        backTargetOverride="/harbors/details?tab=tourism-highlights"
        permissionScope="tourism"
        enablePoiHarborConnection
        embedded
        hideComments
        embeddedRouteId={viewState.id}
        onBack={() => setViewStateWithUrl({ mode: 'list' })}
      />
    );
  }

  if (viewState.mode === 'view') {
    const type: ThemeRouteType = 'harbor';
    return (
      <ThemeRouteFormPage
        mode="view"
        fixedHarborId={selectedHarborId}
        routeType={type}
        routingMode="straight"
        backTargetOverride="/harbors/details?tab=tourism-highlights"
        permissionScope="tourism"
        enablePoiHarborConnection
        embedded
        hideComments
        embeddedRouteId={viewState.id}
        onBack={() => setViewStateWithUrl({ mode: 'list' })}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between gap-2 mb-4">
        <h1 className="text-base font-semibold text-gray-900">
          Tourism Highlights
        </h1>
        {canCreate && (
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => setViewStateWithUrl({ mode: 'create' })}
          >
            New Highlight
          </button>
        )}
      </div>

      <div className="card card-grid min-w-full">
        <div className="card-body">
          <DataGrid
            columns={columns}
            data={harborRoutes}
            rowSelect={false}
            loading={isLoading}
            pagination={{ size: 10 }}
            aria-label="Tourism highlights table"
          />
        </div>
      </div>
    </>
  );
};

export default TourismHighlightsTab;
